'use client';
import { memo } from 'react';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { Filter as FilterType } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import { mapFiltersForDisplay } from '@core/Utils/SearchFilters/MapFiltersForDisplay';
import Button from '@gds/Button/Button';
import { ChevronDown, ChevronTop, Filter, Location } from '@gds/Icons';
import Pill from '@gds/Pill/Pill';
import { useGetRoot } from '@search/Hooks/UseGetRoot';
import { useAtom, useSetAtom } from 'jotai';

import { useRouter } from 'next/navigation';

import { filterTrayOpenAtom } from 'Atoms/App/Plp/FilterTrayOpenAtom';
import { openQuickFilterAtom } from 'Atoms/App/Plp/QuickFilterOpenAtom';
import { useUpdateFilters } from 'Hooks/SearchFilters/UseUpdateFilters';

import { CANT_DELETE } from '../Filters/Components/ActiveFiltersOverview/ActiveFiltersOverview.constants';
import { PlpCombinedFiltersMessages } from '../Filters/FilterPanel.entity';
import { trackFilterEvent } from '../Filters/Filters.tracking';

import { StickyFilterMenus } from './StickyFilterMenus';

import styles from './QuickFilters.module.css';

interface QuickFiltersProps {
  strings: PlpCombinedFiltersMessages;
  locale: Locale;
  filtersList: FilterType[];
  savedPostcode: string;
}

export const QuickFilters = memo(
  ({ strings, locale, savedPostcode, filtersList }: QuickFiltersProps) => {
    type FilterId =
      | 'price'
      | 'location'
      | 'ecom'
      | 'color'
      | 'seats'
      | 'make'
      | 'model'
      | 'fuelType'
      | 'gearBox'
      | 'postcode'
      | 'distance'
      | 'd2c'
      | 'ecom';

    const root = useGetRoot();
    const router = useRouter();

    const [isFiltersOpen, setIsFiltersOpen] = useAtom(filterTrayOpenAtom);
    const [openQuickFilter, setOpenQuickFilter] = useAtom(openQuickFilterAtom);
    const setFiltersList = useSetAtom(filtersListAtom);
    const toggleFilter = (filterName: string) => {
      setOpenQuickFilter(prevOpenFilter => (prevOpenFilter === filterName ? null : filterName));
    };
    const { updateFilters } = useUpdateFilters();

    const filtersConfig = {
      fr: ['make', 'model', 'fuelType', 'price', 'gearBox'],
      uk: ['make', 'model', 'fuelType', 'price', 'gearBox'],
      // TODO: check to add de filters
      de: ['make', 'model', 'fuelType', 'price', 'gearBox'],
    };
    const activeFilters = filtersConfig[locale];

    let filtersLength = mapFiltersForDisplay(filtersList, locale).length;
    const areaFilter = filtersList.find(filter => filter.id === 'area');
    const regionFilter = filtersList.find(filter => filter.id === 'region');
    const dealershipFilter = filtersList.find(filter => filter.id === 'dealership-id');
    if (areaFilter) {
      filtersLength -= 3;
    } else if (regionFilter || dealershipFilter) {
      filtersLength -= 2;
    }

    const locationFilter = filtersList.find(
      filter => filter.id === 'postcode' || filter.id === 'max-distance',
    );
    const priceFilter = filtersList.find(
      filter =>
        filter.id === 'monthly-price__gte' ||
        filter.id === 'monthly-price__lte' ||
        filter.id === 'price__gte' ||
        filter.id === 'price__lte' ||
        filter.id === 'price-reduced',
    );
    const onlyOnHeycar = filtersList.find(filter => filter.id === 'd2c')?.value;
    const buyOnline = filtersList.find(filter => filter.id === 'ecom')?.value;

    const isDisabled = locale === 'uk' && !savedPostcode && !locationFilter;

    const onlyOnHeycarFilter = () => {
      const value = onlyOnHeycar ? undefined : 'true';
      trackFilterEvent('d2c', value ? value : 'false', filtersList);
      updateFilters([{ id: 'd2c', value }]);
    };

    const buyOnlineFilter = () => {
      const value = buyOnline ? undefined : 'true';
      trackFilterEvent('ecom', value ? value : 'false', filtersList);
      updateFilters([{ id: 'ecom', value }]);
    };

    const clearAll = () => {
      const cantDeleteFilters: FilterType[] = filtersList.filter(fltr =>
        CANT_DELETE[locale]?.includes(fltr.id),
      );

      setFiltersList(cantDeleteFilters);
      const newUrl = createPathFromFilters(cantDeleteFilters, locale, root);
      router.push(newUrl);
    };

    const generatePills = (filtersToShow: FilterId[]) => {
      return filtersToShow.map(filterId => {
        const isPriceFilter = filterId === 'price';
        const isLocationFilter = filterId === 'location';
        const isModelFilter = filterId === 'model';
        const isMakeSelected = filtersList.find(filter => filter.id === 'make')?.value;

        const isSelected = isPriceFilter
          ? !!priceFilter
          : isLocationFilter
          ? !!locationFilter
          : !!filtersList.find(f => f.id === filterId);

        const isGreyed = isModelFilter && !isMakeSelected;

        const isFilterOpen = openQuickFilter === filterId;

        const onClick = () => toggleFilter(filterId);

        if (filterId === 'd2c') {
          return (
            <Pill
              key={filterId}
              className={`${styles.pill} ${isSelected ? styles.selected : ''}`}
              onClick={onlyOnHeycarFilter}
            >
              {strings.d2c}
            </Pill>
          );
        }

        if (filterId === 'ecom') {
          return (
            <Pill
              key={filterId}
              className={`${styles.pill} ${isSelected ? styles.selected : ''}`}
              onClick={buyOnlineFilter}
            >
              {strings.ecom}
            </Pill>
          );
        }

        return (
          <Pill
            key={filterId}
            className={`${styles.pill} ${
              isGreyed ? styles.greyed : isSelected ? styles.selected : ''
            }`}
            onClick={onClick}
          >
            {strings[filterId]}
            {isFilterOpen ? <ChevronTop /> : <ChevronDown />}
          </Pill>
        );
      });
    };

    return (
      <>
        <div
          className={`${styles.quickFiltersWrapper} ${
            isDisabled ? styles.postcodeInputStyling : ''
          }`}
        >
          <Pill
            className={`${styles.pill} ${styles.filtersSelected}`}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            dataTestId="filters-pill"
          >
            <Filter />
            {strings.heading}
            {filtersLength > 0 && ` (${filtersLength})`}
          </Pill>

          {isDisabled ? (
            <Pill
              className={`${styles.pill}  ${styles.providePostcode}`}
              onClick={() => toggleFilter('postcode_entry')}
              dataTestId="provide-postcode-pill"
            >
              <Location />
              Enter postcode
            </Pill>
          ) : (
            <>
              {generatePills(activeFilters as FilterId[])}
              <div className={styles.buttons}>
                <Button
                  variant="link"
                  className={styles.viewAll}
                  onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                >
                  {strings.viewAllFilters}
                </Button>
                <div className={styles.divider} />
                <Button variant="link" className={styles.clearAll} onClick={clearAll}>
                  {strings.clearAllBtn}
                </Button>
              </div>
            </>
          )}
        </div>
        <StickyFilterMenus
          openQuickFilter={openQuickFilter}
          strings={strings}
          locale={locale}
          setOpenQuickFilter={setOpenQuickFilter}
        />
      </>
    );
  },
);

QuickFilters.displayName = 'QuickFilters';
